import { ReactNode, useEffect, useRef, useState } from "react";
import { Hr, Padder, SimpleButton, SimpleGhostPeachButton, SimplePageTemplate, SimpleShowingNotShowinButton, SimpleToggleButton } from "./SimpleElements"
import { usePlant, usePlantReducer, usePlantUI } from "../../../biz/hooks/usePlantHooks";
import { DndContext, closestCenter, KeyboardSensor, PointerSensor, useSensor, useSensors, } from '@dnd-kit/core';
import { arrayMove, SortableContext, sortableKeyboardCoordinates, useSortable, verticalListSortingStrategy, } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { useAssortmentsData, useSimpleAssortments } from "./Assortments/useSimpleAssortments";
import { OrganizationOfferType, OrganizationOfferTypeType } from "../../../biz/data/redux/organizationReducer";
import { OkDialog, ProIsActiveDialog, useDialogs } from "./MesssageBoxes";
import { DefaultBanner, LabelEditor, ToggleSvg } from "./SimpleAds";
import { useOrganization } from "../../../biz/hooks/entities/useOrganization";
import { DataHelper, Helpers } from "../../../biz/data/Helpers";
import { useResizeImage } from "../../../biz/data/io/useResizeImage";
import { useAdPreview } from "./Ads/AdPreviews";
import { JwtImage, PlantImageOverlay } from "../main/Images";
import { PlantCheckbox } from "./PlantSearch";
import { useDateFunctions, useFormatting } from "../../../biz/translations/useFormatting";
import { usePricing } from "../../../biz/pricing/usePricing";
import { NullableString, PlantImageType, StringStringDictionary } from "../../../biz/data/Types";
import { useListToggler } from "../../../biz/hooks/ui/useListToggler";
import { AssortmentBanner, OffersBanner, PlantsBanner } from "./SimpleBanners";
import { Dialog } from "../Dialog";
import { AssortmentsUITabStateType } from "../../../biz/data/redux/uIReducer";
import { DateTime } from "luxon";
import { ReadMoreDialog } from "./ReadMoreDialog";
import { useAppData } from "../../../biz/data/useAppData";
import { useTranslation } from "react-i18next";
import { PlantIframeDialog } from "../main/Portal";



export const SimpleAssortments = () => {

    const { t } = useTranslation();
    const ui = useSimpleAssortments();
    const org = useOrganization();
    const proStatus = org.currentOrganizationProStatus;
    const plantUI = usePlantUI();

    useEffect(() => {
        if (!!ui.currentOrganization?.id && proStatus === "pro") {
            ui.loadOffers(ui.currentOrganization?.id);
        }
    }, [, ui.currentOrganization, proStatus]);



    // Add plants
    const onAddPlantPress = () => {
        plantUI.gotoAdPlantsToTarget({
            type: "assortment",
            plantCart: ui.plants,
            cartMaxSize: 100
        });
    }

    const dialog = useDialogs();
    const onMaxButton = (e: AssortmentsUITabStateType) => {
        if (e === "offer") {
            const header = t("maxXActiveOffers", { count: 3 });
            const text = t("youCanCreateAsManyOffersAsYouWantBut_X", { count: 3 });
            const oktext = t("okayIUnderstand");
            dialog.showInfo(
                header,
                text,
                { okText: oktext }
            )
        } else if (e === "selection") {
            dialog.showInfo(
                t("maximumXOffers", { count: 10 }),
                t("yourOfferingIsDisplayedOnYourProfilePageAnd_"),
                { okText: t("okayIUnderstand") }
            )
        }

    }

    const onPublishAll = async (e: AssortmentsUITabStateType) => {
        if (e === "offer") {
            ui.offers?.filter(m => m.state !== "published").forEach(async item => {
                await ui.updateOfferState(org.currentOrganization?.id ?? "", item?.id, item?.state === "draft" ? "published" : "draft")
            });
        } else if (e === "selection") {
            ui.selection?.filter(m => m.state !== "published").forEach(async item => {
                await ui.updateOfferState(org.currentOrganization?.id ?? "", item?.id, item?.state === "draft" ? "published" : "draft")
            });
        }
    }

    const [readMoreVisible, setReadMoreVisible] = useState<boolean>(false);

    return (
        <div>
            <TopSelector items={[
                { id: "offers", label: t("offers"), onClick: () => ui.setUiTabState("offer"), selected: ui.uiTabState === "offer" },
                { id: "selection", label: t("selection"), onClick: () => ui.setUiTabState("selection"), selected: ui.uiTabState === "selection" },
                { id: "plants", label: t("plants"), onClick: () => ui.setUiTabState("plants"), selected: ui.uiTabState === "plants" },
            ]} />

            <SimplePageTemplate className="assortmentmanager">



                {proStatus === "pro" && (
                    <div>
                        {ui.uiTabState === "offer" && (
                            <div>
                                <div className="header-with-add-container">
                                    <h1>
                                        {t("offers")}
                                    </h1>
                                    <SimpleButton
                                        onClick={() => ui.onAddOfferPress("offer")}
                                        label={t("add")}
                                        type="secondarybeige20"
                                        icon="plus"
                                        shape="none"
                                    />

                                    <SimpleButton
                                        onClick={() => onMaxButton("offer")}
                                        label={t("maxXActive", { count: 3 })}
                                        type="tertiarybeige50"
                                        icon="info"
                                        shape="none"
                                    />
                                </div>
                                <Padder heightPx={32} />
                                <Hr />
                                <Padder heightPx={32} />
                                <OfferTools
                                    countOffers={ui.offers?.length}
                                    countNotPublished={ui.offers?.filter(m => m.state !== "published")?.length}
                                    showPublishAll={ui.offers?.length > ui.offers?.filter(m => m.state === "published")?.length}
                                    type={ui.uiTabState}
                                    onPublishAll={() => onPublishAll("offer")}
                                />


                                <OffersSection
                                    header={t("assortment_offers")}
                                    actionButtonText={t("assortment_add_offers")}
                                    infoButtonText={"Max 3"}
                                    onActionPress={() => ui.onAddOfferPress("offer")}>
                                </OffersSection>

                                <Padder heightPx={32} />
                                <DefaultBanner
                                    title={t("offers")}
                                    text={t("offers_banner_text")}
                                    image="./gfx/offer.banner.png"
                                    imageDoubleSize={true}
                                />
                            </div>
                        )}
                        {ui.uiTabState === "selection" && (
                            <div>
                                <div className="header-with-add-container">
                                    <h1>
                                        {t("selection")}
                                    </h1>
                                    <SimpleButton
                                        onClick={() => ui.onAddOfferPress("selection")}
                                        label={t("add")}
                                        type="secondarybeige20"
                                        icon="plus"
                                        shape="none"
                                    />

                                    <SimpleButton
                                        onClick={() => onMaxButton("selection")}
                                        label={t("maxXActive", { count: 10 })}
                                        type="tertiarybeige50"
                                        icon="info"
                                        shape="none"
                                    />



                                </div>
                                <Padder heightPx={32} />
                                <Hr />
                                <Padder heightPx={32} />
                                <OfferTools
                                    countOffers={ui.selection?.length}
                                    countNotPublished={ui.selection?.filter(m => m.state !== "published")?.length}
                                    showPublishAll={ui.selection?.length > ui.selection?.filter(m => m.state === "published")?.length}
                                    type={ui.uiTabState}
                                    onPublishAll={() => onPublishAll("selection")}
                                />

                                <SelectionSection />
                                <Padder heightPx={64} />
                                <DefaultBanner
                                    title={t("selection")}
                                    text={t("selection_page_banner_text")}
                                    image="./gfx/selection.banner.png"
                                    imageDoubleSize={true}
                                />
                            </div>
                        )}
                        {ui.uiTabState === "plants" && (
                            <div>

                                <PlantsSection onAddPlantPress={onAddPlantPress} />
                                {/* <PlantsBanner onAddPress={onAddPlantPress}
                                    onInfoPress={() => { onMaxButton("plants") }}
                                /> */}

                                <Padder heightPx={32} />
                                <DefaultBanner
                                    title={t("plants")}
                                    text={t("plants_page_banner_text")}
                                    image="./gfx/banner.plant.png"
                                />
                            </div>
                        )}

                    </div>
                )}

                {proStatus !== "pro" && (
                    <div>
                        {ui.uiTabState === "offer" && (
                            <div>
                                <div className="header-with-add-container">
                                    <h1>
                                        {t("offers")}
                                    </h1>
                                    <SimpleButton
                                        onClick={() => setReadMoreVisible(true)}
                                        label={t("add")}
                                        type="secondarybeige20"
                                        icon="plus"
                                    />

                                    <SimpleButton
                                        onClick={() => onMaxButton("offer")}
                                        label={t("maxXActive", { count: 3 })}
                                        type="tertiarybeige50"
                                        icon="info"
                                    />
                                </div>
                                <Padder heightPx={32} />

                                <DefaultBanner
                                    title={t("offers")}
                                    text="När du vill lyfta en kampanj, rabatt eller deal. Erbjudandet kommer visas längst upp på din utbudssida, och kan också boostas så att det visas för alla användare på vår upptäckssida."
                                    image="./gfx/banner.offer.png"
                                />

                            </div>
                        )}

                        {ui.uiTabState === "selection" && (
                            <div>
                                <div className="header-with-add-container">
                                    <h1>
                                        Utbud
                                    </h1>
                                    <SimpleButton
                                        onClick={() => setReadMoreVisible(true)}
                                        label={t("add")}
                                        type="secondarybeige20"
                                        icon="plus"
                                    />

                                    <SimpleButton
                                        onClick={() => onMaxButton("selection")}
                                        label={t("maxXActive", { count: 10 })}
                                        type="tertiarybeige50"
                                        icon="info"
                                    />
                                </div>
                                <Padder heightPx={32} />

                                <DefaultBanner
                                    title={t("selection")}
                                    text={t("selection_page_banner_text")}
                                    image="./gfx/banner.selection.png"
                                />
                            </div>
                        )}

                        {ui.uiTabState === "plants" && (
                            <div>
                                <div className="header-with-add-container">
                                    <h1>
                                        {t("plants")}
                                    </h1>
                                    <SimpleButton
                                        onClick={() => setReadMoreVisible(true)}
                                        label={t("add")}
                                        type="secondarybeige20"
                                        icon="plus"
                                    />

                                    {/* <SimpleButton
                                        onClick={() => onMaxButton("plants")}
                                        label={t("maxXActive", { count: 3 })}
                                        type="tertiarybeige50"
                                        icon="info"
                                    /> */}
                                </div>
                                <Padder heightPx={32} />
                                <DefaultBanner
                                    title={t("plants")}
                                    text={t("plants_page_banner_text")}
                                    image="./gfx/banner.plant.png"
                                />
                            </div>
                        )}
                    </div>
                )}


                {/* <Padder heightPx={64} /> */}

            </SimplePageTemplate>

            <Padder heightPx={48} />

            <ReadMoreDialog visible={readMoreVisible} onDismiss={e => {
                setReadMoreVisible(false);
            }} />


        </div>
    )
}

const OfferTools = ({ countOffers, countNotPublished, type, showPublishAll, onPublishAll }: { countOffers: number, countNotPublished: number, type: AssortmentsUITabStateType, showPublishAll: boolean, onPublishAll(): void }) => {
    const { t } = useTranslation();


    return (
        <>

            <div className="offer-tools">
                <div className="flex-1">
                    <div className="quicksand-3-medium">
                        {t("totalNumber")}
                    </div>
                    <div className="quicksand-4-bold">
                        {type === "offer" && (
                            <>
                                {t("Xoffers", { count: countOffers })}
                            </>
                        )}
                        {type === "selection" && (
                            <>
                                {t("Xselections", { count: countOffers })}
                            </>
                        )}
                    </div>
                </div>


                {/* <div className="">
                <div className="quicksand-4-bold text-right">
                    {ui.offers?.filter(m => m.state !== "published")?.length} är inte publicerade
                </div>
                <div className="quicksand-3-medium text-right">
                    Du kan också  publicera dem var för sig
                </div>
            </div>

            <SimpleButton
                onClick={() => { }}
                label={"Publicera alla"}
                type="primarygreen"
                shape="bigpill"
                icon="check"
                iconRight={true}
            /> */}

                {showPublishAll && (
                    <>
                        <div className="publishtext">
                            <div className="header">
                                {t("XAreNotPublished", { count: countNotPublished })}
                            </div>
                            <div className="text">
                                {t("youCanAlsoPublishThemIndividually")}
                            </div>
                        </div>


                        <SimpleButton
                            onClick={onPublishAll}
                            label={t("publishAll")}
                            type="primarygreen"
                            icon="check"
                            iconRight={true}
                            shape="bigpill"
                            className="flex-pull-right"
                        />
                    </>
                )}


            </div>
        </>
    )
}

type TopSelectorItemType = {
    id: string,
    label: string,
    selected: boolean,
    onClick(): void,
    icon?: ReactNode
}
export const TopSelector = ({ items }: { items: TopSelectorItemType[] }) => {
    return (
        <div className="adselector-container">
            <div className="adselector">
                {items.map(item => (
                    <div key={item?.id} className={"adselector-item" + (item?.selected ? " selected" : "")} onClick={item.onClick}>
                        {item?.icon}
                        {item?.label}
                    </div>
                ))}
            </div>
        </div>
    )
}

// *************************** Offers ***************************
const OffersSection = ({ header, actionButtonText, infoButtonText, onActionPress, children }: { header: string, actionButtonText: string, infoButtonText: string, onActionPress(): void, children?: ReactNode }) => {

    const ui = useSimpleAssortments();
    // const dialogs = useDialogs();
    // const org = useOrganization();

    const offerSensors = useSensors(useSensor(PointerSensor), useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates, }));
    const handleOfferDragEnd = (e: any) => {
        const { active, over } = e;
        if (active.id !== over.id) {

            let items = [...ui.offers];
            const oldIndex = items.findIndex(m => m.id == active.id);
            const newIndex = items.findIndex(m => m.id == over.id);
            let d = arrayMove(items, oldIndex, newIndex);
            d.forEach((n, idx) => n.sorting = idx);
            ui.updateOfferSorting(ui.currentOrganization?.id ?? "", d);
        }
    }

    return (
        <div>
            <div>

                <DndContext sensors={offerSensors} collisionDetection={closestCenter} onDragEnd={handleOfferDragEnd} >
                    <SortableContext items={ui.offers} strategy={verticalListSortingStrategy} >
                        {ui.offers.map(o => <OfferItem key={o.id} id={o?.id} />)}
                    </SortableContext>
                </DndContext>

                {/* {!(ui.offers.length > 0) && (
                    <ItemEmpty header={lang.empty} tagline={"Du har inte lagt till några erbjudanden."} />
                )} */}

            </div>
        </div>
    )
}

const OfferItem = ({ id }: { id: string }) => {
    const ops = useAssortmentsData();
    const { t } = useTranslation();
    const dialogs = useDialogs();
    const item = ops.offersDb?.[id];
    const org = useOrganization();
    const df = useFormatting();
    const d = useDateFunctions();


    // Sorting
    const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: item.id });
    const style = { transform: CSS.Transform.toString(transform), transition, };

    // Image
    const [file, setFile] = useState<File | undefined>(undefined);
    const resizeImage = useResizeImage();
    const inputFile = useRef(null);
    const [imageSrc, setImageSrc] = useState<string | ArrayBuffer | null>(null);
    const onFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
        const f = e.target.files?.[0];
        if (!!f) {
            if (FileReader && f) {
                var fr = new FileReader();
                fr.onload = () => setImageSrc(fr.result);
                const fSmall = await resizeImage.resizeFile(f);
                fr.readAsDataURL(fSmall);
                setFile(fSmall);
            }
        }
    };

    // Boost dialog
    const dialog = useDialogs();
    const [boostDialogVisible, setBoostDialogVisible] = useState<boolean>(false);
    const onBoost = () => {
        if (item?.state === "published") {
            setBoostDialogVisible(true);
        } else {
            dialog.showInfo(
                t("publishFirst"),
                t("inOrderToBoostAnOfferItFirstNeedsToBePublished"),
                { okText: t("okayIUnderstand") }
            );
        }
    }
    const itemIsBoosted = (item?.boostedUntilDatekey ?? 0) > d.getDateKey();

    const removeBoost = async () => {
        await ops.updateOfferBoost(org.currentOrganization?.id ?? "", item?.id, 0);
    }
    const onBoostRemove = () => {
        dialog.showConfirm(
            t("cancelBoost"),
            t("TheActiveBoostPeriodIsSetToEndX_", { until: df.datekeyToDate(item?.boostedUntilDatekey ?? 0) }),
            removeBoost, () => { },
            t("cancelBoost"), t("cancel")
        )
    }

    // Org
    const currentOrg = useOrganization()?.currentOrganization;
    const orgName = currentOrg?.name ?? "";

    return (
        <div ref={setNodeRef} style={{
            ...style,
            position: isDragging ? "relative" : "inherit",
            zIndex: isDragging ? 1000 : 0,
        }} {...attributes}>

            <div id={item?.id} className={"offer-item"} >

                {/* Preview */}
                <div className="offer-item-preview">

                    {item?.type === "offer" && (
                        <OfferPreview offer={item} imageSrc={imageSrc} imageUrl={item?.imageUrl} orgName={orgName} />
                    )}
                    {item?.type === "selection" && (
                        <SelectionPreview offer={item} imageSrc={imageSrc} imageUrl={item?.imageUrl} />
                    )}
                    {!!item?.editing && (
                        <Padder heightPx={32} />
                    )}

                </div>

                {/* Info */}
                <div className={"offer-item-info" + (!!item?.editing ? " editing" : "")}>

                    {/* Editing = true */}
                    {item?.editing && (
                        <div>
                            <div className="upload-c">
                                <div>
                                    <SimpleButton
                                        onClick={() => { (inputFile.current as any)?.click() }}
                                        label={t("upload_image")} type="secondarybeige30" icon="image"
                                        fullWidth={true} />
                                    <input
                                        type='file'
                                        id='file'
                                        ref={inputFile}
                                        style={{ display: 'none' }}
                                        onChange={onFileChange}
                                        accept=".jpg,.jpeg"
                                    />

                                </div>

                                <div className="text">
                                    {t("atLeastXPixels_", { dimensions: "320 x 320" })}
                                </div>

                            </div>
                            <LabelEditor
                                visible={true}
                                title0XLabel={t("assortment_input_header")}
                                title0XLength={0}
                                title0XMultiline={false}
                                value={item?.title01}
                                onChange={e => { ops.updateOffer({ ...item, title01: e }) }}
                            />

                            <LabelEditor
                                visible={true}
                                title0XLabel={t("assortment_input_description")}
                                title0XLength={0}
                                title0XMultiline={true}
                                value={item?.title02}
                                onChange={e => { ops.updateOffer({ ...item, title02: e }) }}
                            />

                            <LabelEditor
                                visible={true}
                                title0XLabel={t("assortment_input_link")}
                                title0XLength={0}
                                title0XMultiline={false}
                                value={item?.link}
                                onChange={e => { ops.updateOffer({ ...item, link: e }) }}
                            />

                            <div className="has-offer-details">
                                <div className="toggler" onClick={e => { ops.updateOffer({ ...item, hasOfferDetails: !item?.hasOfferDetails }) }}>
                                    <ToggleSvg on={item?.hasOfferDetails ?? false} onPress={e => { }} />
                                    <div className="lbl">{t("showBoldText")}</div>
                                    <div className="lbl1">{t("usedToShowPriceTimeLimitOrSimilar")}</div>
                                </div>
                                <div className="input-c">
                                    <input
                                        placeholder={t("assortment_input_price_info")}
                                        className="frm-input"
                                        type="text"
                                        value={item?.title03}
                                        onChange={e => { ops.updateOffer({ ...item, title03: e.target.value }) }}
                                    />
                                </div>
                            </div>

                            <Padder heightPx={48} />

                        </div>
                    )}

                    {/* Buttons, Editing = true */}
                    {!!item?.editing && (
                        <div className="button-row">
                            <SimpleButton
                                onClick={() => ops.upsertOffer(org.currentOrganization?.id ?? "", item, file)}
                                label={t("confirm")}
                                icon="check"
                                type="primarygreen"
                                shape="bigpill"
                            />
                            <SimpleButton onClick={() => {
                                if (DataHelper.isGuid(item?.id)) {
                                    ops.setEditItem(item?.id, false);
                                } else {
                                    ops.removeItem(org.currentOrganization?.id ?? "", item?.id);
                                }
                            }}
                                label={t("cancel")}
                                type="tertiarybeige50"
                                shape="bigpill"
                                icon="times" />
                        </div>
                    )}

                    {/* Editing = false */}
                    {!item?.editing && (
                        <div>
                            <div className="button-row offer-buttons">
                                <SimpleButton
                                    onClick={() => ops.setEditItem(item?.id, true)}
                                    label={t("edit")}
                                    type="secondarybeige20"
                                    icon="pen"
                                />

                                <SimpleButton
                                    onClick={() => dialogs.showConfirm("Ta bort erbjudandet?", "Är du säker på att du vill ta bort erbjudandet?",
                                        () => ops.removeItem(org.currentOrganization?.id ?? "", item?.id))}
                                    label={t("remove")}
                                    type="secondarybeige20"
                                    icon="trash"
                                />

                                <SimpleToggleButton
                                    label={t("published")}
                                    onClick={() => ops.updateOfferState(org.currentOrganization?.id ?? "", item?.id, item?.state === "draft" ? "published" : "draft")}
                                    checked={item.state == "published"}
                                />

                            </div>
                            <Padder heightPx={24} />
                            <div style={{ display: "flex", flexDirection: "row", gap: "32px", alignItems: "anchor-center" }}>
                                <div className="timestamp">
                                    <div className="text">
                                        {t("last_changed_by")}:
                                    </div>
                                    <Padder heightPx={4} />
                                    <div className="ts" title={df.epochToDateTimeLong(item?.sys_datemodified_utc_epoch ?? 0)}>
                                        {org.getUserName(item?.sys_modified_by_user_id)},&nbsp;
                                        {df.epochToFriendlyDatetime(item?.sys_datemodified_utc_epoch ?? 0)}
                                    </div>
                                </div>

                                {item?.type === "offer" && !itemIsBoosted && (
                                    <>
                                        <SimpleButton onClick={onBoost} label={t("boost")} type="secondary" icon="flash" />
                                        <InfoIco />
                                    </>
                                )}

                                {item?.type === "offer" && itemIsBoosted && (
                                    <div className="boosted-until-container">
                                        <div className="boosted-until" onClick={onBoostRemove}>
                                            <div className="card">
                                                <svg xmlns="http://www.w3.org/2000/svg" width="25" height="26" viewBox="0 0 25 26" fill="none">
                                                    <path d="M16.2891 3C17.0312 3 17.5781 3.74219 17.3828 4.44531L15.2734 11.75H18.6328C19.375 11.75 19.9609 12.375 19.9609 13.1172C19.9609 13.5469 19.8047 13.8984 19.4922 14.1719L9.375 22.7656C9.17969 22.9219 8.94531 23 8.67188 23C7.92969 23 7.38281 22.2969 7.57812 21.5938L9.6875 14.25H6.28906C5.58594 14.25 5 13.6641 5 12.9609C5 12.5703 5.15625 12.2188 5.42969 11.9453L15.5859 3.27344C15.7812 3.11719 16.0156 3 16.2891 3ZM14.8828 6.32031L7.8125 12.375H10.9375C11.2109 12.375 11.4844 12.5312 11.6797 12.7656C11.8359 13 11.9141 13.3125 11.8359 13.5859L10.0781 19.7188L17.2266 13.625H14.0625C13.75 13.625 13.4766 13.5078 13.2812 13.2734C13.125 13.0391 13.0469 12.7266 13.125 12.4531L14.8828 6.32031Z" fill="#394C39" />
                                                </svg>
                                            </div>
                                            <div className="text">
                                                <div>
                                                    {t("boosted")}
                                                    <span className="note">
                                                        {df.datekeyToDate(item.boostedUntilDatekey ?? 0)}
                                                    </span>
                                                </div>
                                            </div>
                                        </div>
                                        <InfoIco />
                                    </div>
                                )}

                                <BoostDialog
                                    visible={boostDialogVisible}
                                    item={item}
                                    onClose={async e => {
                                        console.log("boostDialog:", e.updated ? e.target : null);
                                        setBoostDialogVisible(false);
                                        if (e.updated) {
                                            await ops.updateOfferBoost(org.currentOrganization?.id ?? "", item?.id, e.target);
                                        }
                                    }}
                                />
                            </div>
                        </div>
                    )}

                </div>

                {/* DragnDrop handle */}
                {!item?.editing && (<div className="offer-item-drag" {...listeners}>
                    <svg width="24" height="53" viewBox="0 0 24 53" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path d="M0 2.5C0 1.39543 0.895431 0.5 2 0.5H22C23.1046 0.5 24 1.39543 24 2.5C24 3.60457 23.1046 4.5 22 4.5H2C0.89543 4.5 0 3.60457 0 2.5Z" fill="#DACFBE" />
                        <path d="M0 14.5C0 13.3954 0.895431 12.5 2 12.5H22C23.1046 12.5 24 13.3954 24 14.5C24 15.6046 23.1046 16.5 22 16.5H2C0.89543 16.5 0 15.6046 0 14.5Z" fill="#DACFBE" />
                        <path d="M0 26.5C0 25.3954 0.895431 24.5 2 24.5H22C23.1046 24.5 24 25.3954 24 26.5C24 27.6046 23.1046 28.5 22 28.5H2C0.89543 28.5 0 27.6046 0 26.5Z" fill="#DACFBE" />
                        <path d="M0 38.5C0 37.3954 0.895431 36.5 2 36.5H22C23.1046 36.5 24 37.3954 24 38.5C24 39.6046 23.1046 40.5 22 40.5H2C0.89543 40.5 0 39.6046 0 38.5Z" fill="#DACFBE" />
                        <path d="M0 50.5C0 49.3954 0.895431 48.5 2 48.5H22C23.1046 48.5 24 49.3954 24 50.5C24 51.6046 23.1046 52.5 22 52.5H2C0.89543 52.5 0 51.6046 0 50.5Z" fill="#DACFBE" />
                    </svg>
                </div>)}
            </div>
        </div>
    )
}

const InfoIco = () => {
    const d = useDialogs();
    const { t } = useTranslation();
    const show = () => {
        d.showInfo(
            t("boostViewing"),
            t("aBoostedOfferIsDisplayedToAllUsers_")
        );
    }
    return (
        <div className="info-icon" onClick={show}>
            <svg xmlns="http://www.w3.org/2000/svg" width="25" height="25" viewBox="0 0 25 26" fill="none">
                <path d="M12.5195 4.875C9.58984 4.875 6.93359 6.4375 5.44922 8.9375C4.00391 11.4766 4.00391 14.5625 5.44922 17.0625C6.93359 19.6016 9.58984 21.125 12.5195 21.125C15.4102 21.125 18.0664 19.6016 19.5508 17.0625C20.9961 14.5625 20.9961 11.4766 19.5508 8.9375C18.0664 6.4375 15.4102 4.875 12.5195 4.875ZM12.5195 23C8.92578 23 5.64453 21.125 3.84766 18C2.05078 14.9141 2.05078 11.125 3.84766 8C5.64453 4.91406 8.92578 3 12.5195 3C16.0742 3 19.3555 4.91406 21.1523 8C22.9492 11.125 22.9492 14.9141 21.1523 18C19.3555 21.125 16.0742 23 12.5195 23ZM10.957 16.125H11.8945V13.625H10.957C10.4102 13.625 10.0195 13.2344 10.0195 12.6875C10.0195 12.1797 10.4102 11.75 10.957 11.75H12.832C13.3398 11.75 13.7695 12.1797 13.7695 12.6875V16.125H14.082C14.5898 16.125 15.0195 16.5547 15.0195 17.0625C15.0195 17.6094 14.5898 18 14.082 18H10.957C10.4102 18 10.0195 17.6094 10.0195 17.0625C10.0195 16.5547 10.4102 16.125 10.957 16.125ZM12.5195 10.5C11.8164 10.5 11.2695 9.95312 11.2695 9.25C11.2695 8.58594 11.8164 8 12.5195 8C13.1836 8 13.7695 8.58594 13.7695 9.25C13.7695 9.95312 13.1836 10.5 12.5195 10.5Z" fill="#9F8E73" />
            </svg>
        </div>
    )
}

const BoostDialog = ({ visible, item, onClose }: { visible: boolean, item: OrganizationOfferType, onClose(e: { target: number, updated: boolean }): void }) => {

    const [boostDays, setBoostDays] = useState<number>(0);
    const d = useDateFunctions();
    const { t } = useTranslation();
    const [today, setToday] = useState<number>(0);
    const [target, setTarget] = useState<number>(0);
    const pricing = usePricing();
    useEffect(() => {
        setToday(d.getDateKey());
        if ((item?.boostedUntilDatekey ?? 0) > 0) {
            const _now = d.getDateKey();
            const _then = item.boostedUntilDatekey ?? 0;
            const _diff = d.daysBetweenDateKeys(_now, _then);
            setBoostDays(d.daysBetweenDateKeys(d.getDateKey(), item.boostedUntilDatekey ?? 0) ?? 0);
        } else {
            setBoostDays(0);
        }
    }, [, visible]);

    useEffect(() => {
        setTarget(d.addDaysToDateKey(today, boostDays) ?? 0);
    }, [visible, boostDays]);

    const dateString = DateTime.now().plus({ days: Math.max(0, boostDays) }).toLocaleString(DateTime.DATE_SHORT);

    const _onClose = (e: { target: number, updated: boolean }) => {
        if (e.target === today) {
            onClose({ ...e, target: 0 });
        } else {
            onClose(e);
        }
    }


    return (
        <Dialog visible={visible} header={t("boostViewing")}
            className="boost-dialog"
            onCancel={() => _onClose({ target: 0, updated: false })}
            clickOutside={() => _onClose({ target: 0, updated: false })}
        >
            <div>
                {t("aBoostedOfferIsDisplayedToAllUsersOnOurDiscoveryPage_")}
            </div>
            <div className="booster">
                <div className="text">
                    {t("Xdays", { count: Math.max(0, boostDays) })}:&nbsp;
                    <span className="tagline">
                        {t("endsAtX", { when: dateString })}
                    </span>
                </div>
                <input
                    className="slider"
                    type="range"
                    min={0}
                    max={90}
                    step={1}
                    value={Math.max(boostDays)}
                    onChange={e => setBoostDays(Math.max(0, e.target.valueAsNumber))}
                />
            </div>
            <Padder heightPx={32} />
            <div className="buttons">
                <SimpleButton onClick={() => _onClose({ target: 0, updated: false })}
                    label={t("cancel")} type="tertiary" shape="bigpill" />
                <SimpleButton
                    onClick={() => _onClose({ target: target, updated: true })}
                    label={t("boostForX", { amount: pricing.costForBoostDays(Math.max(0, boostDays)) })}
                    type="primary"
                    shape="bigpill"
                />
            </div>

        </Dialog>
    )
}

const OfferPreview = ({ offer, imageSrc, imageUrl, orgName }: { offer: OrganizationOfferType, imageSrc: string | ArrayBuffer | null, imageUrl: string, orgName: string }) => {
    const cssBg = useAdPreview({
        imageSrc: imageSrc,
        imageUrl: imageUrl,
        sys_datemodified_utc_epoch: offer?.sys_datemodified_utc_epoch ?? 0,
        // gradientDefinition: "linear-gradient(180deg, rgba(38, 27, 23, 0.08) 0%, rgba(38, 27, 23, 0.40) 100%)"
        gradientDefinition: "linear-gradient(180deg, rgba(0, 0, 0, 0.0) 0%, rgba(0, 0, 0, 0.0) 0%)"
    });
    const { t } = useTranslation();

    return (
        <div className="ad-preview">
            <div className={"type-offer"}>

                <div className="first-col" style={{
                    backgroundImage: cssBg.backgroundImageWithGradient,
                    backgroundPosition: 'center',
                    backgroundSize: 'cover',
                    backgroundRepeat: 'no-repeat',
                    // opacity: processing ? 0.5 : 1
                }}>
                </div>

                <div className="last-col">
                    <div className="container">
                        <div className="annons-header">{orgName}</div>
                        <Padder heightPx={8} />
                        <div className="rubrik">{offer?.title01 || t("assortment_input_header")}</div>
                        <Padder heightPx={8} />
                        <div className="beskrivning">{offer?.title02 || t("assortment_input_description")}</div>
                        {!!offer?.hasOfferDetails && (
                            <>
                                <Padder heightPx={8} />
                                <div className="underrubrik">{offer?.title03 || t("assortment_input_price_info")}</div>
                            </>
                        )}
                    </div>
                </div>
                <div className="perf">

                </div>



            </div>
        </div>

    )
}

// *************************** Selection ***************************
const SelectionSection = () => {


    const ui = useSimpleAssortments();

    const dialogs = useDialogs();
    const selectionSensors = useSensors(useSensor(PointerSensor), useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates, }));
    const handleSelectionDragEnd = (e: any) => {
        const { active, over } = e;
        if (active.id !== over.id) {

            let items = [...ui.selection];
            const oldIndex = items.findIndex(m => m.id == active.id);
            const newIndex = items.findIndex(m => m.id == over.id);
            let d = arrayMove(items, oldIndex, newIndex);
            d.forEach((n, idx) => n.sorting = idx);
            ui.updateOfferSorting(ui.currentOrganization?.id ?? "", d);
        }
    }

    return (
        <div>
            {/* <div className="offer-panel">
                <div className="flex1">
                    <h2>
                        {lang.assortment_selection}
                    </h2>
                </div>
                <div>
                    <SimpleGhostPeachButton
                        onClick={() => {
                            dialogs.showInfo(
                                "Max 10 poster?",
                                "Ditt utbud visas på din profilsida och är tänkt fungera som ett skyltfönster, och bör endast visa ett axplock av de varor eller tjänster du erbjuder. "
                            );
                        }}
                        label={"Max 10"} />

                </div>
                <div className="ml-16">
                    <SimpleButton onClick={() => ui.onAddOfferPress("selection")} label={"Lägg till Utbud"} type="primary" icon="plus" />
                </div>
            </div> */}

            <DndContext sensors={selectionSensors} collisionDetection={closestCenter} onDragEnd={handleSelectionDragEnd} >
                <SortableContext items={ui.selection} strategy={verticalListSortingStrategy} >
                    {ui.selection.map(o => <OfferItem key={o.id} id={o?.id} />)}
                </SortableContext>
            </DndContext>

            {/* {!(ui.selection.length > 0) && (
                <ItemEmpty header={lang.empty} tagline={"Du har inte lagt till något utbud."} />
            )} */}
        </div>
    )
}


const SelectionPreview = ({ offer, imageSrc, imageUrl }: { offer: OrganizationOfferType, imageSrc: string | ArrayBuffer | null, imageUrl: string }) => {
    const cssBg = useAdPreview({
        imageSrc: imageSrc,
        imageUrl: imageUrl,
        sys_datemodified_utc_epoch: offer?.sys_datemodified_utc_epoch ?? 0,
        gradientDefinition: "linear-gradient(180deg, rgba(0, 0, 0, 0.0) 0%, rgba(0, 0, 0, 0.0) 0%)"
    });

    return (
        <div className="ad-preview">
            <div className={"type-assortment"}>

                <div className="first-col" style={{
                    backgroundImage: cssBg.backgroundImageWithGradient,
                    backgroundPosition: 'center',
                    backgroundSize: 'cover',
                    backgroundRepeat: 'no-repeat',
                }}>
                </div>

                <div className="last-col">
                    <div className="container">
                        <div className="rubrik">{offer?.title01 || "Rubrik"}</div>
                        <Padder heightPx={8} />
                        <div className="beskrivning">{offer?.title02 || "Beskrivning"}</div>
                        {!!offer?.hasOfferDetails && (
                            <>
                                <Padder heightPx={8} />
                                <div className="underrubrik">{offer?.title03 || "PrisInfo"}</div>
                            </>
                        )}
                    </div>
                </div>



            </div>
        </div>

    )
}

// *************************** Plants ***************************

export const ELEMENT_PLANTSSECTION_ID = "plant-section"
const PlantsSection = ({ onAddPlantPress }: { onAddPlantPress(): void }) => {
    const ui = useSimpleAssortments();
    const plant = usePlantReducer();
    const { t } = useTranslation();

    useEffect(() => {
        if (Object.keys(ui.plantLinks ?? {})?.length > 0) {
            plant.loadPlantIds(Object.keys(ui.plantLinks ?? {}));
        }
    }, [, ui.plantLinks]);

    const [plantImageId, setPlantImageId] = useState<PlantImageType>(undefined);
    const toggler = useListToggler();

    // Filter
    const [search, setSearch] = useState<string>("");
    const [filteredPlants, setFilteredPlants] = useState<string[]>(ui.plants);

    const sortPlanIds = (a: string, b: string) => {
        const ap = plant.db?.[a]; const bp = plant.db?.[b];
        const ak = ap?.name_lc?.trim() + ap?.specifictype_lc?.trim();
        const bk = bp?.name_lc?.trim() + bp?.specifictype_lc?.trim();
        if (!ak || !bk) { return 0; }
        return ak.localeCompare(bk);
    };

    useEffect(() => {
        if (Object.keys(ui.plantsDb ?? {}).length > 0) {
            if (!!search) {
                let pids = ui.plants.filter(id => {
                    const p = plant.db?.[id];
                    const s = p?.name?.toLowerCase() + ";" +
                        p?.specifictype?.toLowerCase() + ";" +
                        p?.familyname?.toLowerCase() + ";" +
                        p?.type?.toLowerCase() + ";" +
                        p?.namelatin_lc;
                    return s.includes(search?.toLowerCase());
                });
                pids.sort(sortPlanIds);
                setFilteredPlants(pids);
            } else {
                let pids = [...ui.plants];
                pids.sort(sortPlanIds);
                setFilteredPlants(pids);
            }
        }
    }, [, search, ui.plantsDb]);

    const [linkDialogSettings, setLinkDialogSettings] = useState<{
        visible?: boolean,
        link?: string,
        linkValid?: boolean,
        plantId?: string
    }>({ visible: false });

    useEffect(() => {
        if (linkDialogSettings?.visible && (linkDialogSettings?.link?.startsWith("https://") || !linkDialogSettings?.link)) {
            setLinkDialogSettings({ ...linkDialogSettings, linkValid: true });
        } else {
            setLinkDialogSettings({ ...linkDialogSettings, linkValid: false });
        }
    }, [linkDialogSettings?.link]);


    return (
        <div id={ELEMENT_PLANTSSECTION_ID}>
            <div>
                <PlantsSectionHeader search={search} setSearch={setSearch} onAddPress={onAddPlantPress} />
            </div>

            <PlantImageOverlay plantImageid={plantImageId?.imageid} onClick={() => setPlantImageId(undefined)} />

            {(ui.plants?.length > 0) && (
                <table className="tbl-plants">
                    <PlantTableHeader />
                    <tbody>
                        {filteredPlants?.map(id => (
                            <PlantRow
                                key={id} id={id}
                                selected={toggler.isInList(id) && false}
                                onRemove={() => ui.removePlantFromAssortment(id)}
                                imageClicked={e => setPlantImageId(e)}
                                rowClicked={toggler.toggleListIem}
                                plantLink={ui.plantLinks?.[id]}
                                onSetPlantLink={() => {
                                    const link = ui.plantLinks?.[id] ?? "";
                                    setLinkDialogSettings({ visible: true, plantId: id, link: link.startsWith("https://") ? link : "" });
                                }}
                            />
                        ))}
                    </tbody>
                </table>
            )}

            <Dialog
                visible={!!linkDialogSettings?.visible}
                header={t("linkToYourPlant")}
                onCancel={() => setLinkDialogSettings({ visible: false })}
                cancelText={t("cancel")}
                okText={t("save")}
                onOk={async () => {
                    await ui.setPlantLink(linkDialogSettings?.plantId ?? "", !!linkDialogSettings.link ? linkDialogSettings.link : (linkDialogSettings.plantId ?? ""));
                    setLinkDialogSettings({ visible: false });
                }}
                okDisabled={!linkDialogSettings?.linkValid}
                // clickOutside={() => { }}
                type="input-link"
            >
                <div>
                    {t("linkPlantDialogText")}
                </div>

                <Padder heightPx={16} />
                <label>
                    {t("uRLToThePlantOnYourWebsite")}
                </label>
                <div>
                    <input type="text" className="frm-input" value={linkDialogSettings?.link}
                        onChange={e => setLinkDialogSettings({ ...linkDialogSettings, link: e.target.value })}
                        placeholder="Ex. https://www.dinbutik.se/froer/prastkrage...."
                    />
                </div>
            </Dialog>

            <PlantIframeDialog plantId={plantImageId?.plantId} close={() => setPlantImageId(undefined)} />
            {/* {!(ui.plants?.length > 0) && (
                <ItemEmpty header={lang.empty} tagline={"Du har inte lagt till några växter."} />
            )} */}

        </div>
    )
}

const PlantsSectionHeader = ({ search, setSearch, onAddPress }: { search: string, setSearch(e: string): void, onAddPress(): void }) => {
    const { t } = useTranslation();
    const ui = useSimpleAssortments();
    const pricing = usePricing();
    const numberOfPlants = ui.plants?.length ?? 0;

    const dialogs = useDialogs();

    const { supportEmail, numberOfPlantsCount } = useAppData();

    return (
        <div>
            <div className="plant-section-header-buttons">
                <h1>
                    {t("myPlants")}
                </h1>
                <SimpleButton
                    onClick={onAddPress}
                    label={t("add")}
                    type="secondarybeige20"
                    icon="plus"
                    shape="none"
                />
                <div>
                    <SimpleButton onClick={() => {
                        dialogs.showInfo(
                            t("prices"),
                            t("thePricePerPlantIsXPerMonthAndIsAddedToYourMonthlyInvoice_", { price: (pricing.getPriceString(pricing.productsPrice.assortment_month_price)) })
                        )

                    }}
                        label={t("prices")}
                        type="tertiarybeige50"
                        icon="info"
                        shape="none"
                    />
                </div>
                <div>
                    <SimpleButton onClick={() => {
                        dialogs.showInfo(
                            t("manyPlants"),
                            t("ohGardenContainsALibraryOfOver_", {
                                plantcount: numberOfPlantsCount,
                                supportEmail: supportEmail
                            })
                        )
                    }}
                        label={t("manyPlants")}
                        type="tertiarybeige50"
                        icon="info"
                        shape="none"
                    />
                </div>
                <div className="info">
                    <div className="label">{t("yourHoldings")}</div>
                    <div className="value">{t("Xplants", { count: numberOfPlants })}</div>
                </div>
                <div className="info">
                    <div className="label">{t("cost")}</div>
                    <div className="value">{pricing.assortmentPlantsMonthPriceString(numberOfPlants) + " " + t("perMonth")}</div>
                </div>

            </div>

            <Padder heightPx={32} />
            <div className="hr-2" />
            <Padder heightPx={32} />

            {numberOfPlants > 0 && (
                <div className="plant-local-search">
                    <div className="container">
                        <input
                            className="frm-input"
                            type="text" placeholder={t("searchYourExistingHoldings")}
                            value={search}
                            onChange={e => setSearch(e.target.value)}
                            style={{ maxWidth: "750px" }}
                        />
                        <div className="search-icon">
                            <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 20 20" fill="none">
                                <path d="M13.5039 8.49219C13.5039 6.71094 12.5352 5.08594 11.0039 4.17969C9.44141 3.27344 7.53516 3.27344 6.00391 4.17969C4.44141 5.08594 3.50391 6.71094 3.50391 8.49219C3.50391 10.3047 4.44141 11.9297 6.00391 12.8359C7.53516 13.7422 9.44141 13.7422 11.0039 12.8359C12.5352 11.9297 13.5039 10.3047 13.5039 8.49219ZM12.5352 13.6172C11.4102 14.4922 10.0039 14.9922 8.50391 14.9922C4.91016 14.9922 2.00391 12.0859 2.00391 8.49219C2.00391 4.92969 4.91016 1.99219 8.50391 1.99219C12.0664 1.99219 15.0039 4.92969 15.0039 8.49219C15.0039 10.0234 14.4727 11.4297 13.5977 12.5547L17.7852 16.7109C18.0664 17.0234 18.0664 17.4922 17.7852 17.7734C17.4727 18.0859 17.0039 18.0859 16.7227 17.7734L12.5352 13.6172Z" fill="#9F8E73" />
                            </svg>
                        </div>
                    </div>
                </div>
            )}

        </div>

    )
}

const PlantsSectionHeaderValue = ({ label, value }: { label: string, value: string }) => {
    return (
        <div className="vals">
            <div>
                <div className="label">{label}</div>
            </div>
            <div>
                <div className="value">{value}</div>
            </div>
        </div>
    )
}

type ColumnHeaderClickedType = "name" | "specifictype" | "namelatin" | "planttypeid" | "familyname" | "grupperingText";
const PlantTableHeader = () => {
    const { t } = useTranslation();
    const [sortColumn, setSortColumn] = useState<string>("");
    const [sortOrder, setSortOrder] = useState<"asc" | "desc" | undefined>();

    const onClick = (e: ColumnHeaderClickedType) => {
        if (e === sortColumn) {
            setSortOrder("desc")
        } else {
            setSortColumn(e);
            setSortOrder("asc");
        }
    }

    // useEffect(() => {
    //     console.log("sortColumn,sortOrder:", sortColumn, sortOrder);
    // }, [sortColumn, sortOrder]);
    return (
        <thead>
            <tr>
                <th></th>
                <th onClick={() => onClick("name")} id="col-name" className="td-name">
                    <div>{t("name")}</div>
                    <div className="thin">{t("andAlternativeName")}</div>
                </th>
                <th onClick={() => onClick("specifictype")} id="col-specifictype">{t("plant_field_sort")}</th>
                <th onClick={() => onClick("namelatin")} id="col-namelatin">{t("plant_field_latin_name")}</th>
                <th onClick={() => onClick("planttypeid")} id="col-planttypeid">{t("plant_field_type")}</th>
                <th onClick={() => onClick("familyname")} id="col-familyname">{t("plant_field_family")}</th>
                <th onClick={() => onClick("grupperingText")} id="col-grupperingText" className="hide">{t("plant_field_group")}</th>
                <th></th>
            </tr>
        </thead>
    )
}

const PlantRow = ({ id, selected, onRemove, imageClicked, rowClicked, plantLink, onSetPlantLink }: { id: string, selected: boolean, onRemove(): void, imageClicked(e: PlantImageType): void, rowClicked(e: string): void, plantLink: string, onSetPlantLink(): void }) => {
    const plant = usePlantReducer();
    const p = plant.db?.[id];
    const dialog = useDialogs();

    const onClick = () => {
        rowClicked(p?.id)
    }

    if (!p) { return (null); }

    return (
        <tr className={"plant" + (selected ? " selected" : "")}>
            <td className="td-image" onClick={() => {
                if (!!p?.imageid) {
                    imageClicked({
                        plantId: p?.id,
                        imageid: p?.imageid
                    })
                }
            }}>
                <JwtImage
                    imageId={p?.imageid}
                    plantImage={true}
                    format={"thumb"}
                    title={p?.name + " " + p?.specifictype}
                    imageText={p?.name + " " + p?.specifictype}
                />
            </td>
            <td className="td-name" onClick={() => onClick()}>
                <div className="name">
                    {p?.name}
                </div>
                <div className="alternativenames">
                    {p?.alternativenames}
                </div>
            </td>

            <td onClick={() => onClick()}>
                <div>{p?.specifictype}</div>
                <div>{p?.alternativename_type}</div>

            </td>
            <td onClick={() => onClick()}>
                <div>{p?.namelatin}</div>
                <div>{p?.alternativename_latin}</div>
            </td>
            <td onClick={() => onClick()}>{p?.planttypeid}</td>
            <td onClick={() => onClick()}>{p?.familyname}</td>
            <td onClick={() => onClick()}>{p?.grupperingText}</td>
            <td className="td-separator"></td>
            <td style={{ verticalAlign: "middle", padding: "0" }}>
                <div className="tools">
                    {/* Separator */}
                    <div>
                        <svg width="2" height="40" viewBox="0 0 2 40" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <path d="M0 0.999999C0 0.447714 0.447715 0 1 0C1.55228 0 2 0.447715 2 1V39C2 39.5523 1.55228 40 1 40C0.447715 40 0 39.5523 0 39V0.999999Z" fill="#E7DED0" />
                        </svg>
                    </div>

                    <LinkIcon onClick={onSetPlantLink} plantLink={plantLink} />

                    {/* Separator */}
                    <div>
                        <svg width="2" height="40" viewBox="0 0 2 40" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <path d="M0 0.999999C0 0.447714 0.447715 0 1 0C1.55228 0 2 0.447715 2 1V39C2 39.5523 1.55228 40 1 40C0.447715 40 0 39.5523 0 39V0.999999Z" fill="#E7DED0" />
                        </svg>
                    </div>

                    {/* Trashcan */}
                    <div
                        style={{ alignContent: "center", }}
                        onClick={() => { dialog.showConfirm("Ta bort", "Ta bort växten?", onRemove) }}
                    >
                        <svg xmlns="http://www.w3.org/2000/svg" width="25" height="26" viewBox="0 0 25 26" fill="none">
                            <path d="M10.3906 5.03125L9.64844 6.125H15.3125L14.5703 5.03125C14.5312 4.95312 14.4141 4.875 14.2969 4.875H10.6641C10.5469 4.875 10.4297 4.95312 10.3906 5.03125ZM16.1328 3.97656L17.5781 6.125H18.125H20H20.3125C20.8203 6.125 21.25 6.55469 21.25 7.0625C21.25 7.60938 20.8203 8 20.3125 8H20V19.875C20 21.6328 18.5938 23 16.875 23H8.125C6.36719 23 5 21.6328 5 19.875V8H4.6875C4.14062 8 3.75 7.60938 3.75 7.0625C3.75 6.55469 4.14062 6.125 4.6875 6.125H5H6.875H7.38281L8.82812 3.97656C9.21875 3.39062 9.92188 3 10.6641 3H14.2969C15.0391 3 15.7422 3.39062 16.1328 3.97656ZM6.875 8V19.875C6.875 20.5781 7.42188 21.125 8.125 21.125H16.875C17.5391 21.125 18.125 20.5781 18.125 19.875V8H6.875ZM10 10.5V18.625C10 18.9766 9.6875 19.25 9.375 19.25C9.02344 19.25 8.75 18.9766 8.75 18.625V10.5C8.75 10.1875 9.02344 9.875 9.375 9.875C9.6875 9.875 10 10.1875 10 10.5ZM13.125 10.5V18.625C13.125 18.9766 12.8125 19.25 12.5 19.25C12.1484 19.25 11.875 18.9766 11.875 18.625V10.5C11.875 10.1875 12.1484 9.875 12.5 9.875C12.8125 9.875 13.125 10.1875 13.125 10.5ZM16.25 10.5V18.625C16.25 18.9766 15.9375 19.25 15.625 19.25C15.2734 19.25 15 18.9766 15 18.625V10.5C15 10.1875 15.2734 9.875 15.625 9.875C15.9375 9.875 16.25 10.1875 16.25 10.5Z" fill="#9F8E73" />
                        </svg>
                    </div>
                </div>
            </td>
        </tr>
    )

}

const LinkIcon = ({ plantLink, onClick }: { plantLink: string, onClick(): void }) => {
    const hasUrl = !!(plantLink?.startsWith("http"));
    if (hasUrl) {
        return (
            <div className="link-plant" onClick={onClick}>
                <svg width="33" height="33" viewBox="0 0 33 34" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M0 8.5C0 4.08172 3.58172 0.5 8 0.5H25C29.4183 0.5 33 4.08172 33 8.5V25.5C33 29.9183 29.4183 33.5 25 33.5H8C3.58172 33.5 0 29.9183 0 25.5V8.5Z" fill="#DBE0D7" />
                    <path d="M26.6758 17.4492L22.2617 21.8632C20.0352 24.0507 16.4805 24.0507 14.293 21.8632C12.2227 19.7539 12.1055 16.4335 14.0195 14.207L14.2148 13.9726C14.5664 13.582 15.1523 13.5429 15.543 13.8945C15.9336 14.246 15.9727 14.832 15.6211 15.2226L15.4648 15.4179C14.1367 16.9023 14.2148 19.1289 15.6211 20.5351C17.0664 21.9804 19.4492 21.9804 20.9336 20.5351L25.3477 16.121C26.793 14.6367 26.793 12.2929 25.3477 10.8085C23.9414 9.44135 21.7148 9.36323 20.2305 10.6523L19.9961 10.8476C19.6055 11.1992 19.0195 11.1601 18.668 10.7695C18.3164 10.3789 18.3555 9.79292 18.7461 9.44136L18.9805 9.24604C21.207 7.29292 24.5664 7.41011 26.6758 9.48042C28.8633 11.6679 28.8633 15.2226 26.6758 17.4492ZM6.32422 16.5507L10.7773 12.1367C12.9648 9.94917 16.5195 9.94917 18.707 12.1367C20.8164 14.207 20.9336 17.5664 18.9805 19.7929L18.7461 20.0273C18.4336 20.4179 17.8086 20.496 17.418 20.1445C17.0273 19.7929 16.9883 19.207 17.3398 18.8164L17.5742 18.582C18.8633 17.0976 18.7852 14.871 17.3789 13.4648C15.9336 11.9804 13.5508 11.9804 12.1055 13.4648L7.65234 17.8789C6.20703 19.3242 6.20703 21.707 7.65234 23.1914C9.05859 24.5585 11.2852 24.6367 12.7695 23.3476L13.0039 23.1523C13.3945 22.8007 13.9805 22.8398 14.332 23.2304C14.6836 23.621 14.6445 24.207 14.2539 24.5585L14.0195 24.7539C11.793 26.707 8.43359 26.5898 6.32422 24.5195C4.13672 22.332 4.13672 18.7382 6.32422 16.5507Z" fill="#506750" />
                </svg>


            </div>
        )
    }
    return (
        <div className="link-plant" onClick={onClick}>
            <svg width="33" height="33" viewBox="0 0 33 33" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M26.6758 16.9492L22.2617 21.3632C20.0352 23.5507 16.4805 23.5507 14.293 21.3632C12.2227 19.2539 12.1055 15.9335 14.0195 13.707L14.2148 13.4726C14.5664 13.082 15.1523 13.0429 15.543 13.3945C15.9336 13.746 15.9727 14.332 15.6211 14.7226L15.4648 14.9179C14.1367 16.4023 14.2148 18.6289 15.6211 20.0351C17.0664 21.4804 19.4492 21.4804 20.9336 20.0351L25.3477 15.621C26.793 14.1367 26.793 11.7929 25.3477 10.3085C23.9414 8.94135 21.7148 8.86323 20.2305 10.1523L19.9961 10.3476C19.6055 10.6992 19.0195 10.6601 18.668 10.2695C18.3164 9.87885 18.3555 9.29292 18.7461 8.94136L18.9805 8.74604C21.207 6.79292 24.5664 6.91011 26.6758 8.98042C28.8633 11.1679 28.8633 14.7226 26.6758 16.9492ZM6.32422 16.0507L10.7773 11.6367C12.9648 9.44917 16.5195 9.44917 18.707 11.6367C20.8164 13.707 20.9336 17.0664 18.9805 19.2929L18.7461 19.5273C18.4336 19.9179 17.8086 19.996 17.418 19.6445C17.0273 19.2929 16.9883 18.707 17.3398 18.3164L17.5742 18.082C18.8633 16.5976 18.7852 14.371 17.3789 12.9648C15.9336 11.4804 13.5508 11.4804 12.1055 12.9648L7.65234 17.3789C6.20703 18.8242 6.20703 21.207 7.65234 22.6914C9.05859 24.0585 11.2852 24.1367 12.7695 22.8476L13.0039 22.6523C13.3945 22.3007 13.9805 22.3398 14.332 22.7304C14.6836 23.121 14.6445 23.707 14.2539 24.0585L14.0195 24.2539C11.793 26.207 8.43359 26.0898 6.32422 24.0195C4.13672 21.832 4.13672 18.2382 6.32422 16.0507Z" fill="#9F8E73" />
            </svg>

        </div>
    )
}

// *************************** Shared ***************************
const ItemEmpty = ({ header, tagline }: { header: string, tagline: string }) => {
    return (
        <div className="offer-item empty">

            <div className="offer-item-empty-header">
                {header}
            </div>

            <div className="offer-item-empty-tagline">
                {tagline}
            </div>

        </div>
    )
}


